import { Button, Card, ErrorState, Icon, Spinner } from "@elit-hair/elit-ui";
import { Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarEvent, TbSettings, TbUsers } from "react-icons/tb";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDashboardCalendarCapacities } from "@/hooks/queries/calendar-capacities.queries";
import { CalendarCapacitiesSelector } from "@/pages/performance/calendar-capacities/components/calendar-capacities-selector";
import { useCalendarCapacitiesSelection } from "@/pages/performance/calendar-capacities/hooks/use-calendar-capacities-selection";
import {
    CalendarCapacitiesSelectorVariant,
    filterVisibleCalendarRows,
} from "@/pages/performance/calendar-capacities/utils/calendar-capacities.utils";

/** Occupancy (0 = empty, 100 = full). Bar length and hue follow this so “full” reads as full/green, low occupancy as short/red. */
function capacityBarClassFromOccupiedPercent(occupiedPercent: number): string {
    if (occupiedPercent <= 10) return "bg-destructive/80";
    if (occupiedPercent <= 30) return "bg-amber-500/90";
    return "bg-emerald-500/80";
}

function CalendarCapacityNameCell({
    label,
    color,
    agentCount,
    calendarId,
}: {
    label: string;
    color: string;
    agentCount: number;
    calendarId?: string;
}) {
    const content = (
        <>
            <span className="w-1 self-stretch rounded-full shrink-0 opacity-90" style={{ backgroundColor: color }} />
            <div className="min-w-0 flex-1">
                <div
                    className={
                        calendarId
                            ? "font-medium text-foreground truncate group-hover:text-primary transition-colors"
                            : "font-medium text-foreground truncate"
                    }
                >
                    {label}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Icon icon={TbUsers} className="size-3.5 shrink-0 opacity-80" aria-hidden />
                    <span className="tabular-nums">{agentCount}</span>
                </div>
            </div>
        </>
    );

    if (calendarId) {
        return (
            <Link
                to="/calendars/$id"
                params={{ id: calendarId }}
                className="min-w-0 group flex items-center gap-2 text-left"
            >
                {content}
            </Link>
        );
    }

    return <div className="min-w-0 flex items-center gap-2 text-left">{content}</div>;
}

function CapacityDayBlock({ booked, max }: { booked: number; max: number }) {
    const occupiedPercent = max > 0 ? Math.max(0, Math.min(100, Math.round((booked / max) * 100))) : null;
    const label = max > 0 ? `${booked}/${max}` : "—";
    const barClass = occupiedPercent === null ? "bg-muted" : capacityBarClassFromOccupiedPercent(occupiedPercent);

    return (
        <div className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-baseline justify-end gap-1">
                <span className="text-lg font-semibold tabular-nums tracking-tight text-foreground">{label}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ${barClass}`}
                    style={{ width: occupiedPercent === null ? "0%" : `${occupiedPercent}%` }}
                />
            </div>
        </div>
    );
}

type CalendarCapacitiesWidgetProps = {
    hideHeader?: boolean;
    enabled?: boolean;
};

function formatCapacityDateLabel(isoDate: string | undefined, fallback: string): string {
    if (!isoDate) return fallback;
    const parsed = DateTime.fromISO(isoDate);
    return parsed.isValid ? parsed.toFormat("dd MMM") : fallback;
}

export const CalendarCapacitiesWidget = ({ hideHeader = false, enabled = true }: CalendarCapacitiesWidgetProps) => {
    const { t } = useTranslation();
    const { selectedCalendars } = useCalendarCapacitiesSelection();
    const {
        rows: allRows,
        todayDate,
        tomorrowDate,
        isLoading,
        isError,
        error,
        refetch,
        isEmptySnapshot,
    } = useDashboardCalendarCapacities({ enabled });

    const rows = useMemo(() => filterVisibleCalendarRows(allRows, selectedCalendars), [allRows, selectedCalendars]);

    if (isLoading) {
        return (
            <Card className="flex h-full flex-col overflow-hidden rounded-2xl border-border/60 bg-linear-to-br from-card via-card to-muted/20 p-6 shadow-sm">
                <div className="flex min-h-44 flex-1 items-center justify-center">
                    <Spinner size="md" />
                </div>
            </Card>
        );
    }

    if (isError) {
        return (
            <Card className="p-6 overflow-hidden border-border/60 bg-linear-to-br from-card via-card to-muted/20 shadow-sm rounded-2xl">
                <ErrorState
                    title={t("calendar_capacities_load_error")}
                    message={error instanceof Error ? error.message : t("calendar_capacities_load_error_message")}
                    onRetry={() => void refetch()}
                />
            </Card>
        );
    }

    if (isEmptySnapshot) {
        return (
            <Card className="p-6 overflow-hidden border-border/60 bg-linear-to-br from-card via-card to-muted/20 shadow-sm rounded-2xl">
                <div className="flex flex-col items-center justify-center gap-3 text-center py-6">
                    <div className="rounded-full bg-muted/60 p-3">
                        <Icon icon={TbCalendarEvent} className="size-7 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground max-w-xs">{t("calendar_capacities_no_snapshot")}</p>
                    <Button variant="outline" size="sm" onClick={() => void refetch()}>
                        {t("refresh")}
                    </Button>
                </div>
            </Card>
        );
    }

    if (rows.length === 0 && !isLoading) {
        return (
            <Card className="p-6 overflow-hidden border-border/60 bg-linear-to-br from-card via-card to-muted/20 shadow-sm rounded-2xl">
                <div className="flex flex-col gap-4">
                    {!hideHeader ? (
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground tracking-tight">
                                    {t("calendar_capacities_title")}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {t("calendar_capacities_subtitle")}
                                </p>
                            </div>
                            <CalendarCapacitiesMenu />
                        </div>
                    ) : (
                        <CalendarCapacitiesSelector variant={CalendarCapacitiesSelectorVariant.INLINE} />
                    )}
                    <p className="text-sm text-muted-foreground text-center py-4">
                        {selectedCalendars.length === 0
                            ? t("calendar_capacities_select_calendars")
                            : t("calendar_capacities_empty_list")}
                    </p>
                </div>
            </Card>
        );
    }

    return (
        <Card className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border/60 bg-linear-to-br from-card via-card to-muted/15 p-0 shadow-sm">
            {!hideHeader ? (
                <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border/50 bg-muted/5 px-5 pb-3 pt-5">
                    <div>
                        <h3 className="text-lg font-semibold tracking-tight text-foreground">
                            {t("calendar_capacities_title")}
                        </h3>
                        <p className="mt-0.5 text-xs text-muted-foreground">{t("calendar_capacities_subtitle")}</p>
                    </div>
                    <CalendarCapacitiesMenu />
                </div>
            ) : (
                <div className="shrink-0 border-b border-border/50 bg-muted/5 px-5 pb-3 pt-4">
                    <CalendarCapacitiesSelector variant={CalendarCapacitiesSelectorVariant.INLINE} />
                </div>
            )}

            <div className="grid shrink-0 grid-cols-[minmax(0,1.4fr)_1fr_1fr] gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span className="pl-2">{t("calendar_capacities_column_calendar")}</span>
                <span className="text-center">
                    {formatCapacityDateLabel(todayDate, t("calendar_capacities_today"))}
                </span>
                <span className="text-center">
                    {formatCapacityDateLabel(tomorrowDate, t("calendar_capacities_tomorrow"))}
                </span>
            </div>
            <div className="min-h-0 flex-1 divide-y divide-border/40 overflow-y-auto px-2 pb-3">
                {rows.map((row) => (
                    <div
                        key={row.name}
                        className="grid grid-cols-[minmax(0,1.4fr)_1fr_1fr] gap-2 py-3 px-2 items-center rounded-lg hover:bg-muted/30 transition-colors"
                    >
                        <CalendarCapacityNameCell
                            label={row.label}
                            color={row.color}
                            agentCount={row.agentCount}
                            calendarId={row.calendarId}
                        />
                        <div className="px-1">
                            <CapacityDayBlock booked={row.todayBooked} max={row.todayMax} />
                        </div>
                        <div className="px-1">
                            <CapacityDayBlock booked={row.tomorrowBooked} max={row.tomorrowMax} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="px-5 py-3 border-t border-border/40 bg-muted/10">
                <p className="text-[11px] leading-relaxed text-muted-foreground">{t("calendar_capacities_footnote")}</p>
            </div>
        </Card>
    );
};

function CalendarCapacitiesMenu() {
    const { t } = useTranslation();

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="flex items-center justify-center h-7 w-7 shrink-0 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    aria-label={t("configure_calendars")}
                >
                    <Icon icon={TbSettings} />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
                <CalendarCapacitiesSelector />
            </PopoverContent>
        </Popover>
    );
}
